import React from "react";
import { graphql } from "gatsby";
import parse from "html-react-parser";
import { useContentfulCaseStudy } from "@js-utils/contentful/casestudy";
import Meta from "@components/Meta";

import { InitialColorProvider } from "@components/layout/SectionColor";
import { MultiVideoConductor } from "@components/Video";

import {
  HeroSection,
  FreeformSection,
  QuoteSection,
  CreditsSection,
  RelatedProjects,
} from "@components/case-study/sections";
import {
  TextBlock,
  ImageBlock,
  VideoBlock,
  ImageGridBlock,
  TextAndImageBlock,
  DoubleTextAndImageBlock,
  CarouselBlock,
} from "@components/case-study/blocks";

export default function CaseStudy(props) {
  const project = useContentfulCaseStudy();
  const { hero, sections, credits, relatedProjects, pageMeta } = project(
    props.data.contentfulCaseStudy,
  );
  const { awards } = props.data.contentfulCaseStudy;

  return (
    <>
      <Meta
        pageTitle={hero.title}
        pageMeta={
          pageMeta
            ? pageMeta
            : {
                pageTitle: `${hero.title}`,
                description: { description: `${hero.summary}` },
                socialImage: {
                  file: {
                    url:
                      hero.hero.type == "image" ? hero.hero.content.src : hero.hero.content.poster,
                  },
                },
              }
        }
      />
      <InitialColorProvider
        backgroundColor={hero.backgroundColor}
        accentColor={hero.accentColor}
        isDark={hero.isDark}
      />
      <MultiVideoConductor>
        <HeroSection
          ruleLabel={hero.ruleLabel}
          title={hero.title}
          client={hero.client}
          description={hero.description && parse(hero.description, { trim: true })}
          hero={hero.hero}
          data-page-background-color={hero.backgroundColor}
          data-page-accent-color={hero.accentColor}
          data-page-is-dark={hero.isDark}
          awards={awards}
        />

        {sections.map(({ id, type, ...section }) => {
          const sectionColorAttrs = {
            "data-page-background-color": section.backgroundColor,
            "data-page-accent-color": section.accentColor,
            "data-page-is-dark": section.isDark,
          };

          if (type === "quote") {
            return (
              <QuoteSection key={id} attribution={section.attribution} {...sectionColorAttrs}>
                {section.quote}
              </QuoteSection>
            );
          }
          if (type === "freeform") {
            return (
              <FreeformSection
                key={id}
                title={section.title}
                description={section.description && parse(section.description, { trim: true })}
                ruleLabel={section.ruleLabel}
                {...sectionColorAttrs}
              >
                {(section.blocks || []).map(({ id: blockId, type: blockType, ...block }) => {
                  if (blockType === "text")
                    return (
                      <TextBlock key={blockId} label={block.label} layout={block.layout}>
                        {parse(block.content, { strip: true })}
                      </TextBlock>
                    );
                  if (blockType === "image")
                    return (
                      <ImageBlock
                        key={blockId}
                        src={block.src}
                        alt={block.alt}
                        attribution={block.attribution}
                      />
                    );
                  if (blockType === "text-and-image")
                    return (
                      <TextAndImageBlock
                        key={blockId}
                        image={block.image}
                        text={parse(block.text, { strip: true })}
                        textLabel={block.textLabel}
                        layout={block.layout}
                      />
                    );
                  if (blockType === "double-text-and-image")
                    return (
                      <DoubleTextAndImageBlock
                        key={blockId}
                        first={{
                          textLabel: block.first.textLabel,
                          text: parse(block.first.text, { strip: true }),
                          image: block.first.image,
                        }}
                        second={{
                          textLabel: block.second.textLabel,
                          text: parse(block.second.text, { strip: true }),
                          image: block.second.image,
                        }}
                        layout={block.layout}
                      />
                    );
                  if (blockType === "video")
                    return (
                      <VideoBlock
                        key={blockId}
                        previewSrc={block.src}
                        previewPoster={block.poster}
                        vimeoId={block.vimeoId}
                      />
                    );
                  if (blockType === "image-grid")
                    return (
                      <ImageGridBlock
                        key={blockId}
                        attribution={block.attribution}
                        layout={block.layout}
                        images={block.images}
                      />
                    );
                  if (blockType === "carousel")
                    return <CarouselBlock key={blockId} items={block.items} />;
                })}
              </FreeformSection>
            );
          }
        })}
        {credits.projects && (
          <CreditsSection
            description={credits.description && parse(credits.description, { trim: true })}
            projects={credits.projects}
            data-page-background-color={credits.theme.backgroundColor}
            data-page-accent-color={credits.theme.accentColor}
            data-page-is-dark={credits.theme.isDark}
          />
        )}
      </MultiVideoConductor>

      {/* Related projects */}
      {relatedProjects?.projects?.length > 0 && (
        <RelatedProjects
          projects={relatedProjects.projects}
          data-page-background-color={relatedProjects.backgroundColor}
          data-page-accent-color={relatedProjects.accentColor}
          data-page-is-dark={relatedProjects.isDark}
        />
      )}
    </>
  );
}

export const pageQuery = graphql`
  query ($slug: String) {
    contentfulCaseStudy(slug: { eq: $slug }) {
      id
      pageMeta {
        ...PageMetaData
      }
      awards {
        name
        image {
          file {
            url
          }
          description
        }
      }
      theme {
        ... on Node {
          ... on ContentfulTheme {
            id
            accentColor
            backgroundColor
            theme
          }
          ... on ContentfulCustomTheme {
            id
            accentColor
            backgroundColor
            theme
          }
        }
      }
      ruleLabel
      title {
        title
      }
      capability {
        title
      }
      description {
        description: childMarkdownRemark {
          html
        }
      }
      summary {
        summary
      }
      client {
        clientName
      }
      hero {
        ... on ContentfulVideo {
          __typename
          previewSrc {
            description
            file {
              url
              contentType
            }
          }
          src
          staticPreview {
            file {
              url
              contentType
            }
          }
        }
      }
      credits {
        description {
          childMarkdownRemark {
            html
          }
        }
        projects {
          title
          people {
            name
            title
          }
        }
      }
      sections {
        ... on ContentfulQuote {
          __typename
          id
          author
          role
          quote {
            quote
          }
          theme {
            ... on Node {
              ... on ContentfulTheme {
                id
                accentColor
                backgroundColor
                theme
              }
              ... on ContentfulCustomTheme {
                id
                accentColor
                backgroundColor
                theme
              }
            }
          }
        }
        ... on ContentfulSection {
          id
          theme {
            ... on Node {
              ... on ContentfulTheme {
                id
                accentColor
                backgroundColor
                theme
              }
              ... on ContentfulCustomTheme {
                id
                accentColor
                backgroundColor
                theme
              }
            }
          }
          eyebrow
          title
          description {
            description: childMarkdownRemark {
              html
            }
          }
          blocks {
            ... on Node {
              ... on ContentfulVideo {
                __typename
                id
                previewSrc {
                  description
                  file {
                    url
                    contentType
                  }
                }
                src
                staticPreview {
                  file {
                    url
                    contentType
                  }
                }
              }
              ... on ContentfulImage {
                __typename
                id
                source {
                  file {
                    url
                    contentType
                  }
                  description
                }
                attribution
              }
              ... on ContentfulTextBlock {
                __typename
                id
                layout
                label
                title
                content {
                  childMarkdownRemark {
                    html
                  }
                }
              }
              ... on ContentfulImageGridBlock {
                __typename
                id
                layout
                images {
                  id
                  description
                  file {
                    url
                    contentType
                  }
                }
                attribution
              }
              ... on ContentfulCarousel {
                __typename
                id
                items {
                  ... on ContentfulImage {
                    id
                    attribution
                    source {
                      description
                      file {
                        url
                        contentType
                      }
                    }
                  }
                  ... on ContentfulVideo {
                    id
                    previewSrc {
                      file {
                        url
                        contentType
                      }
                    }
                  }
                }
              }
              ... on ContentfulTextAndImageBlock {
                __typename
                id
                layout
                TextAndImageContent: content {
                  id
                  label
                  title
                  text {
                    childMarkdownRemark {
                      html
                    }
                  }
                  image {
                    id
                    attribution
                    source {
                      description
                      file {
                        url
                        contentType
                      }
                    }
                  }
                }
              }
              ... on ContentfulDoubleTextAndImage {
                __typename
                id
                layout
                DoubleTextContent: content {
                  id
                  label
                  title
                  text {
                    childMarkdownRemark {
                      html
                    }
                  }
                  image {
                    id
                    attribution
                    source {
                      description
                      file {
                        url
                        contentType
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      relatedProjects {
        ...ProjectOverview
      }
    }
  }
`;
